import React, { useEffect, useMemo, useState } from "react";
import { Button, Dialog, IconButton, MenuItem, TextField } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import EventAvailableRoundedIcon from "@material-ui/icons/EventAvailableRounded";
import TodayRoundedIcon from "@material-ui/icons/TodayRounded";
import ScheduleRoundedIcon from "@material-ui/icons/ScheduleRounded";
import PlaceRoundedIcon from "@material-ui/icons/PlaceRounded";
import { toast } from "react-toastify";
import {
  addDays,
  addMonths,
  endOfMonth,
  endOfWeek,
  format,
  isBefore,
  isSameMonth,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import ptBR from "date-fns/locale/pt-BR";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import useEstilosModal from "./estilosModal";

const PRIMEIRA_HORA = 6;
const ULTIMA_HORA = 22;
const LOTE = 400;

const FAIXAS = {
  manha: [6 * 60, 12 * 60],
  tarde: [12 * 60, 18 * 60],
  noite: [18 * 60, 22 * 60],
};

const hhmm = (min) => `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`;
const paraMinutos = (texto) => {
  const [h, m] = String(texto || "0:0").split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
};
const chaveDia = (d) => format(d, "yyyy-MM-dd");

const useEstilosCalendario = makeStyles((theme) => {
  const escuro = theme.palette.type === "dark";
  const linha = escuro ? "rgba(255,255,255,.10)" : "#e2e8f0";
  return {
    duasColunas: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
      gap: 16,
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "minmax(0, 1fr)" },
    },
    mesTopo: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 },
    mesNome: { fontWeight: 800, fontSize: 15, color: theme.palette.text.primary },
    semana: { display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 },
    nomeDia: {
      textAlign: "center",
      fontSize: 11,
      fontWeight: 800,
      color: theme.palette.text.secondary,
      padding: "4px 0",
    },
    dia: {
      position: "relative",
      height: 40,
      borderRadius: 10,
      border: `1px solid ${linha}`,
      background: "transparent",
      cursor: "pointer",
      fontFamily: "inherit",
      fontWeight: 700,
      fontSize: 14,
      color: theme.palette.text.primary,
      transition: "transform .1s ease, background .15s ease",
      "&:hover": { transform: "translateY(-1px)", borderColor: "#0D9488" },
      "&:disabled": { opacity: 0.3, cursor: "not-allowed", transform: "none", borderColor: linha },
    },
    diaFora: { opacity: 0.35 },
    diaHoje: { borderColor: "#0D9488", borderWidth: 2 },
    diaAtivo: {
      background: "linear-gradient(135deg,#14b8a6,#0D9488)",
      borderColor: "transparent",
      color: "#fff",
      boxShadow: "0 4px 12px rgba(13,148,136,.35)",
    },
    marcaExistente: {
      position: "absolute",
      bottom: 4,
      left: "50%",
      transform: "translateX(-50%)",
      width: 5,
      height: 5,
      borderRadius: "50%",
      background: "#60a5fa",
    },
    atalhos: { display: "flex", gap: 6, flexWrap: "wrap", margin: "10px 0" },
    atalho: {
      border: `1px solid ${linha}`,
      background: "transparent",
      borderRadius: 999,
      padding: "4px 12px",
      fontSize: 12,
      fontWeight: 700,
      cursor: "pointer",
      color: theme.palette.text.secondary,
      fontFamily: "inherit",
      "&:hover": { borderColor: "#0D9488", color: "#0D9488" },
    },
    horas: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(64px, 1fr))",
      gap: 6,
      maxHeight: 260,
      overflowY: "auto",
      paddingRight: 4,
    },
    hora: {
      height: 34,
      borderRadius: 9,
      border: `1px solid ${linha}`,
      background: "transparent",
      cursor: "pointer",
      fontFamily: "inherit",
      fontWeight: 700,
      fontSize: 13,
      color: theme.palette.text.primary,
      "&:hover": { borderColor: "#0D9488" },
      "&:disabled": {
        cursor: "not-allowed",
        textDecoration: "line-through",
        opacity: 0.35,
      },
    },
    horaRemover: {
      background: "#e53935",
      borderColor: "transparent",
      color: "#fff",
    },
    abasModo: {
      display: "inline-flex",
      alignSelf: "flex-start",
      padding: 4,
      gap: 4,
      borderRadius: 12,
      background: escuro ? "rgba(255,255,255,.05)" : "#eef2f0",
    },
    abaModo: {
      border: "none",
      background: "none",
      cursor: "pointer",
      fontFamily: "inherit",
      fontSize: 13,
      fontWeight: 800,
      padding: "7px 16px",
      borderRadius: 9,
      color: theme.palette.text.secondary,
    },
    abaModoAtiva: { background: "linear-gradient(135deg,#14b8a6,#0D9488)", color: "#fff" },
    abaModoRemover: { background: "#e53935", color: "#fff" },
    horaAtiva: {
      background: "linear-gradient(135deg,#14b8a6,#0D9488)",
      borderColor: "transparent",
      color: "#fff",
    },
    legenda: { fontSize: 11.5, color: theme.palette.text.secondary, marginTop: 6 },
  };
});

const estadoInicial = () => ({
  duracao: 30,
  title: "",
  location: "",
  interviewerId: "",
  notes: "",
});

const NovoHorarioModal = ({ open, inicial, modoInicial, usuarios, onClose, onSalvo, onRemovido }) => {
  const classes = useEstilosModal();
  const cal = useEstilosCalendario();

  const [form, setForm] = useState(estadoInicial);
  const [mes, setMes] = useState(() => startOfMonth(new Date()));
  const [dias, setDias] = useState([]); // "yyyy-MM-dd"
  const [horas, setHoras] = useState([]); // minutos desde 00:00
  const [existentes, setExistentes] = useState({}); // dia -> Map(minuto -> horario)
  const [modo, setModo] = useState("cadastrar"); // cadastrar | remover
  const [salvando, setSalvando] = useState(false);
  const [recarregar, setRecarregar] = useState(0);

  const hoje = startOfDay(new Date());

  // Abriu: pre-seleciona o dia (e as horas) que vieram de um clique ou
  // arraste no calendario grande.
  useEffect(() => {
    if (!open) return;
    const base = estadoInicial();
    setForm(base);
    setModo(modoInicial || "cadastrar");
    if (inicial?.data) {
      const d = parseISO(inicial.data);
      setMes(startOfMonth(d));
      setDias(isBefore(d, hoje) ? [] : [inicial.data]);
      const ini = paraMinutos(inicial.horaInicio);
      const fim = Math.max(paraMinutos(inicial.horaFim), ini + base.duracao);
      const lista = [];
      for (let t = ini; t + base.duracao <= fim; t += base.duracao) lista.push(t);
      setHoras(inicial.horaInicio ? lista : []);
    } else {
      setMes(startOfMonth(new Date()));
      setDias([]);
      setHoras([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, inicial, modoInicial]);

  // Horarios ja cadastrados no mes visivel (bolinha no dia e hora riscada).
  useEffect(() => {
    if (!open) return;
    const ini = startOfWeek(mes, { weekStartsOn: 0 });
    const fim = addDays(endOfWeek(endOfMonth(mes), { weekStartsOn: 0 }), 1);
    api
      .get("/interviews", { params: { start: ini.toISOString(), end: fim.toISOString() } })
      .then(({ data }) => {
        const mapa = {};
        (data || []).forEach((s) => {
          const d = new Date(s.startAt);
          const k = chaveDia(d);
          if (!mapa[k]) mapa[k] = new Map();
          mapa[k].set(d.getHours() * 60 + d.getMinutes(), s);
        });
        setExistentes(mapa);
      })
      .catch(() => setExistentes({}));
  }, [open, mes, recarregar]);

  const celulas = useMemo(() => {
    const ini = startOfWeek(mes, { weekStartsOn: 0 });
    const fim = endOfWeek(endOfMonth(mes), { weekStartsOn: 0 });
    const lista = [];
    for (let d = ini; d <= fim; d = addDays(d, 1)) lista.push(d);
    return lista;
  }, [mes]);

  // Grade de horarios: comeca nas horas cheias e anda de acordo com a
  // duracao, mas sempre inclui as horas que vieram do arraste.
  // Modo remover: so os horarios que existem nos dias escolhidos. Cada um
  // guarda os registros (de todos os dias) que serao apagados.
  const doRemover = useMemo(() => {
    const mapa = new Map();
    dias.forEach((k) => {
      (existentes[k] || new Map()).forEach((slot, t) => {
        if (!mapa.has(t)) mapa.set(t, []);
        mapa.get(t).push(slot);
      });
    });
    return mapa;
  }, [dias, existentes]);

  const removivel = (t) => (doRemover.get(t) || []).some((sl) => sl.status !== "booked");

  const opcoesHoraCadastro = useMemo(() => {
    const passo = Number(form.duracao) || 30;
    const lista = [];
    for (let t = PRIMEIRA_HORA * 60; t + passo <= ULTIMA_HORA * 60; t += passo) lista.push(t);
    horas.forEach((t) => {
      if (!lista.includes(t)) lista.push(t);
    });
    return lista.sort((a, b) => a - b);
  }, [form.duracao, horas]);

  const opcoesHora =
    modo === "remover" ? Array.from(doRemover.keys()).sort((a, b) => a - b) : opcoesHoraCadastro;

  const trocarModo = (novo) => {
    setModo(novo);
    setHoras([]);
  };

  const mudarDuracao = (e) => {
    const nova = Number(e.target.value);
    setForm((f) => ({ ...f, duracao: nova }));
    // As horas marcadas so continuam se couberem na grade nova.
    setHoras((lista) => lista.filter((t) => (t - PRIMEIRA_HORA * 60) % nova === 0));
  };

  const alternarDia = (d) => {
    const k = chaveDia(d);
    setDias((lista) => (lista.includes(k) ? lista.filter((x) => x !== k) : [...lista, k].sort()));
  };

  const alternarHora = (t) =>
    setHoras((lista) => (lista.includes(t) ? lista.filter((x) => x !== t) : [...lista, t].sort((a, b) => a - b)));

  const marcarFaixa = (faixa) => {
    const [de, ate] = FAIXAS[faixa];
    const passo = Number(form.duracao) || 30;
    const daFaixa = opcoesHora.filter(
      (t) => t >= de && (modo === "remover" ? t < ate && removivel(t) : t + passo <= ate)
    );
    const todasMarcadas = daFaixa.every((t) => horas.includes(t));
    setHoras((lista) =>
      todasMarcadas
        ? lista.filter((t) => !daFaixa.includes(t))
        : Array.from(new Set([...lista, ...daFaixa])).sort((a, b) => a - b)
    );
  };

  const selecionarDiasUteis = () => {
    const lista = celulas
      .filter((d) => isSameMonth(d, mes) && !isBefore(d, hoje) && d.getDay() >= 1 && d.getDay() <= 5)
      .map(chaveDia);
    setDias(lista);
  };

  // Hora riscada: quando so um dia esta selecionado e ele ja tem esse horario
  // (ou o horario de hoje ja passou).
  const agora = new Date();
  const jaExiste = (t) => modo === "cadastrar" && dias.length === 1 && !!existentes[dias[0]]?.has(t);
  const horaPassada = (t) =>
    modo === "cadastrar" &&
    dias.length === 1 && dias[0] === chaveDia(agora) && t <= agora.getHours() * 60 + agora.getMinutes();

  const horasValidas = horas.filter((t) => !jaExiste(t) && !horaPassada(t));
  const idsParaRemover = horas.flatMap((t) =>
    (doRemover.get(t) || []).filter((sl) => sl.status !== "booked").map((sl) => sl.id)
  );
  const marcadasProtegidas = horas.reduce(
    (n, t) => n + (doRemover.get(t) || []).filter((sl) => sl.status === "booked").length,
    0
  );
  const total = modo === "remover" ? idsParaRemover.length : dias.length * horasValidas.length;

  const remover = async () => {
    if (!idsParaRemover.length) return;
    setSalvando(true);
    try {
      const { data } = await api.post("/interviews/bulk-delete", { ids: idsParaRemover });
      const apagados = data?.deleted?.length || 0;
      toast.success(`${apagados} horário(s) removido(s) da agenda.`);
      setHoras([]);
      setRecarregar((n) => n + 1);
      if (onRemovido) onRemovido();
    } catch (err) {
      toastError(err);
    } finally {
      setSalvando(false);
    }
  };

  const salvar = async () => {
    const duracao = Number(form.duracao) || 30;
    const slots = [];
    dias.forEach((k) => {
      const [a, m, d] = k.split("-").map(Number);
      horasValidas.forEach((t) => {
        const inicio = new Date(a, m - 1, d, Math.floor(t / 60), t % 60);
        if (inicio <= new Date()) return;
        slots.push({
          startAt: inicio.toISOString(),
          endAt: new Date(inicio.getTime() + duracao * 60000).toISOString(),
        });
      });
    });
    if (!slots.length) {
      toast.error("Escolha pelo menos um dia e um horário no futuro.");
      return;
    }

    setSalvando(true);
    let criados = 0;
    let pulados = 0;
    try {
      for (let i = 0; i < slots.length; i += LOTE) {
        // eslint-disable-next-line no-await-in-loop
        const { data } = await api.post("/interviews", {
          slots: slots.slice(i, i + LOTE),
          title: form.title.trim(),
          location: form.location.trim(),
          interviewerId: form.interviewerId || null,
          notes: form.notes.trim(),
        });
        criados += data?.created?.length || 0;
        pulados += data?.skipped || 0;
      }
      if (criados) {
        toast.success(
          `${criados} horário(s) cadastrado(s).${pulados ? ` ${pulados} já existia(m) e foi(ram) ignorado(s).` : ""}`
        );
      } else {
        toast.info("Nenhum horário novo: todos já existiam na agenda.");
      }
      onSalvo();
    } catch (err) {
      toastError(err);
      if (criados) onSalvo();
    } finally {
      setSalvando(false);
    }
  };

  const mudar = (campo) => (e) => {
    const valor = e.target.value;
    setForm((f) => ({ ...f, [campo]: valor }));
  };
  const nomeMes = format(mes, "MMMM 'de' yyyy", { locale: ptBR });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{ className: classes.papel, style: { maxWidth: 820 } }}
    >
      <div className={classes.hero}>
        <div className={classes.heroLinha}>
          <div className={classes.heroIcone}>
            <EventAvailableRoundedIcon />
          </div>
          <div style={{ minWidth: 0 }}>
            <div className={classes.heroEtiqueta}>Agenda de entrevistas</div>
            <div className={classes.heroTitulo}>
              {modo === "remover" ? "Remover horários livres" : "Cadastrar horários livres"}
            </div>
            <div className={classes.heroSub}>
              Escolha os dias no calendário e clique nos horários. A assistente só oferece o que estiver aqui.
            </div>
          </div>
          <IconButton size="small" className={classes.fechar} onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      </div>

      <div className={classes.corpo}>
        <div className={cal.abasModo}>
          <button
            type="button"
            className={`${cal.abaModo} ${modo === "cadastrar" ? cal.abaModoAtiva : ""}`}
            onClick={() => trocarModo("cadastrar")}
          >
            Cadastrar horários
          </button>
          <button
            type="button"
            className={`${cal.abaModo} ${modo === "remover" ? cal.abaModoRemover : ""}`}
            onClick={() => trocarModo("remover")}
          >
            Remover horários
          </button>
        </div>
        <div className={cal.duasColunas}>
          {/* ── Dias ── */}
          <div className={classes.secao}>
            <div className={classes.secaoTopo}>
              <div className={classes.secaoIcone}>
                <TodayRoundedIcon />
              </div>
              <div>
                <div className={classes.secaoTitulo}>1. Escolha os dias</div>
                <div className={classes.secaoSub}>Clique em um ou vários dias.</div>
              </div>
            </div>
            <div className={cal.mesTopo}>
              <IconButton
                size="small"
                onClick={() => setMes((m) => addMonths(m, -1))}
                disabled={!isBefore(startOfMonth(new Date()), mes)}
                aria-label="Mês anterior"
              >
                <ChevronLeftIcon />
              </IconButton>
              <span className={cal.mesNome}>{nomeMes.charAt(0).toUpperCase() + nomeMes.slice(1)}</span>
              <IconButton size="small" onClick={() => setMes((m) => addMonths(m, 1))} aria-label="Próximo mês">
                <ChevronRightIcon />
              </IconButton>
            </div>
            <div className={cal.semana}>
              {["D", "S", "T", "Q", "Q", "S", "S"].map((n, i) => (
                <div key={i} className={cal.nomeDia}>
                  {n}
                </div>
              ))}
              {celulas.map((d) => {
                const k = chaveDia(d);
                const passado = isBefore(d, hoje);
                const ativo = dias.includes(k);
                return (
                  <button
                    key={k}
                    type="button"
                    disabled={passado}
                    title={existentes[k] ? `${existentes[k].size} horário(s) já cadastrado(s)` : undefined}
                    className={[
                      cal.dia,
                      isSameMonth(d, mes) ? "" : cal.diaFora,
                      k === chaveDia(hoje) ? cal.diaHoje : "",
                      ativo ? cal.diaAtivo : "",
                    ].join(" ")}
                    onClick={() => alternarDia(d)}
                  >
                    {format(d, "d")}
                    {existentes[k] && !ativo && <span className={cal.marcaExistente} />}
                  </button>
                );
              })}
            </div>
            <div className={cal.atalhos}>
              <button type="button" className={cal.atalho} onClick={selecionarDiasUteis}>
                Seg a sex do mês
              </button>
              <button type="button" className={cal.atalho} onClick={() => setDias([])}>
                Limpar dias
              </button>
            </div>
            <div className={cal.legenda}>
              <span style={{ color: "#60a5fa" }}>●</span> dia que já tem horários cadastrados
            </div>
          </div>

          {/* ── Horarios ── */}
          <div className={classes.secao}>
            <div className={classes.secaoTopo}>
              <div className={classes.secaoIcone}>
                <ScheduleRoundedIcon />
              </div>
              <div>
                <div className={classes.secaoTitulo}>2. Escolha os horários</div>
                <div className={classes.secaoSub}>
                  {modo === "remover"
                    ? "Clique nos horários que quer apagar."
                    : "Cada horário vira uma entrevista."}
                </div>
              </div>
            </div>
            {modo === "cadastrar" && (
            <TextField
              select
              label="Duração de cada entrevista"
              size="small"
              variant="outlined"
              fullWidth
              className={classes.campo}
              value={form.duracao}
              onChange={mudarDuracao}
            >
              {[15, 20, 30, 40, 45, 60, 90, 120].map((m) => (
                <MenuItem key={m} value={m}>
                  {m < 60 ? `${m} minutos` : `${m / 60} hora${m > 60 ? "s" : ""}`}
                </MenuItem>
              ))}
            </TextField>
            )}
            <div className={cal.atalhos}>
              {modo === "remover" && (
                <button
                  type="button"
                  className={cal.atalho}
                  onClick={() => setHoras(opcoesHora.filter(removivel))}
                >
                  Todos
                </button>
              )}
              <button type="button" className={cal.atalho} onClick={() => marcarFaixa("manha")}>
                Manhã
              </button>
              <button type="button" className={cal.atalho} onClick={() => marcarFaixa("tarde")}>
                Tarde
              </button>
              <button type="button" className={cal.atalho} onClick={() => marcarFaixa("noite")}>
                Noite
              </button>
              <button type="button" className={cal.atalho} onClick={() => setHoras([])}>
                Limpar
              </button>
            </div>
            <div className={cal.horas}>
              {opcoesHora.map((t) => {
                const bloqueada =
                  jaExiste(t) || horaPassada(t) || (modo === "remover" && !removivel(t));
                return (
                  <button
                    key={t}
                    type="button"
                    disabled={bloqueada}
                    title={
                      jaExiste(t)
                        ? "Já cadastrado neste dia"
                        : modo === "remover" && !removivel(t)
                        ? "Entrevista marcada: cancele pelo calendário"
                        : undefined
                    }
                    className={`${cal.hora} ${
                      horas.includes(t) && !bloqueada ? (modo === "remover" ? cal.horaRemover : cal.horaAtiva) : ""
                    }`}
                    onClick={() => alternarHora(t)}
                  >
                    {hhmm(t)}
                  </button>
                );
              })}
              {modo === "remover" && !opcoesHora.length && (
                <div className={cal.legenda} style={{ gridColumn: "1 / -1" }}>
                  {dias.length ? "Nenhum horário cadastrado nesses dias." : "Escolha os dias primeiro."}
                </div>
              )}
            </div>
          </div>
        </div>

        {modo === "remover" ? (
          <div className={classes.resumo} style={{ color: "#e53935", background: "rgba(229,57,53,.08)" }}>
            {total
              ? `Serão removidos ${total} horário(s) livre(s).`
              : dias.length
              ? "Agora clique nos horários que quer remover."
              : "Escolha os dias no calendário."}
            {marcadasProtegidas
              ? ` ${marcadasProtegidas} entrevista(s) marcada(s) nesses horários não será(ão) apagada(s).`
              : ""}
          </div>
        ) : (
        <div className={classes.resumo}>
          {total
            ? `Serão criados ${total} horário(s): ${horasValidas.length} horário(s) em ${dias.length} dia(s).`
            : dias.length
            ? "Agora clique nos horários."
            : "Escolha pelo menos um dia no calendário."}
        </div>
        )}

        {modo === "cadastrar" && (
        <div className={classes.secao}>
          <div className={classes.secaoTopo}>
            <div className={classes.secaoIcone}>
              <PlaceRoundedIcon />
            </div>
            <div>
              <div className={classes.secaoTitulo}>Detalhes (opcional)</div>
              <div className={classes.secaoSub}>A assistente informa o tipo e o local ao candidato.</div>
            </div>
          </div>
          <div className={classes.grade}>
            <TextField
              label="Tipo de entrevista"
              placeholder="Ex.: Entrevista presencial"
              size="small"
              variant="outlined"
              className={classes.campo}
              value={form.title}
              onChange={mudar("title")}
            />
            <TextField
              select
              label="Entrevistador" SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}
              size="small"
              variant="outlined"
              className={classes.campo}
              value={form.interviewerId}
              onChange={mudar("interviewerId")}
            >
              <MenuItem value="">Não definido</MenuItem>
              {(usuarios || []).map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.name}
                </MenuItem>
              ))}
            </TextField>
          </div>
          <TextField
            label="Local ou link da videochamada"
            placeholder="Ex.: Rua das Flores, 100 - Sala 3  |  https://meet.google.com/..."
            size="small"
            variant="outlined"
            fullWidth
            className={classes.campo}
            style={{ marginTop: 12 }}
            value={form.location}
            onChange={mudar("location")}
          />
          <TextField
            label="Observações internas"
            size="small"
            variant="outlined"
            fullWidth
            multiline
            minRows={2}
            className={classes.campo}
            style={{ marginTop: 12 }}
            value={form.notes}
            onChange={mudar("notes")}
          />
        </div>
        )}
      </div>

      <div className={classes.rodape} style={{ justifyContent: "flex-end" }}>
        <Button className={classes.secundario} onClick={onClose} disabled={salvando}>
          Cancelar
        </Button>
        {modo === "remover" ? (
          <Button className={classes.perigoCheio} onClick={remover} disabled={salvando || !total}>
            {salvando ? "Removendo..." : total ? `Remover ${total} horário(s)` : "Remover horários"}
          </Button>
        ) : (
          <Button className={classes.primario} onClick={salvar} disabled={salvando || !total}>
            {salvando ? "Salvando..." : total ? `Cadastrar ${total} horário(s)` : "Cadastrar horários"}
          </Button>
        )}
      </div>
    </Dialog>
  );
};

export default NovoHorarioModal;
